import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Trash2, Briefcase, X, MailCheck } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import UserProfileLink from "@/components/UserProfileLink";
import TaxonomyPicker from "@/components/TaxonomyPicker";

const ROLES = ["client", "student", "mentor", "tutor", "partner_institution", "employee", "admin", "super_admin"];

// Roles that can be granted as *secondary* capabilities on top of a primary role.
// Must stay in sync with the backend whitelist in EmployeeProfileIn.
const SECONDARY_ROLE_OPTIONS = ["employee", "mentor", "tutor", "intern", "partner_institution"];

// Predefined employee roles + organisational teams. Admin can still type a
// custom value in either field.
const PREDEFINED_EMP_ROLES = [
  "CEO", "Co-Founder", "Director", "Marketing Head", "Engineering Research Head",
  "Research Lead", "Operations Head", "HR", "Finance", "Designer", "Engineer", "Intern",
];
const PREDEFINED_TEAMS = [
  "Board of Directors", "Executive Team", "Marketing", "Research",
  "Engineering", "Operations", "People & HR",
];

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const pager = usePager(users, 20);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { user_id, name, employee_role, team_section, khojbyte_email }

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (role) params.role = role;
    const r = await api.get("/admin/users", { params });
    setUsers(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { api.get("/org/units").then((r) => setUnitOptions(r.data || [])).catch(() => {}); }, []);

  const changeRole = async (uid, newRole) => {
    try {
      await api.post(`/admin/users/${uid}/role`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error("Couldn't change role"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?\n\nThis cannot be undone. The user will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const manualVerifyEmail = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/verify-email`);
      toast.success(`${u.name || u.email}'s email marked verified`);
      setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, email_verified: true } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't verify"); }
  };

  const saveEmployeeProfile = async () => {
    if (!editing) return;
    try {
      const secondary = Array.isArray(editing.secondary_roles) ? editing.secondary_roles : [];
      // Derive `is_intern` from the multi-role picker so the two are always
      // in sync — the standalone tick was removed to eliminate duplicate state.
      const payload = {
        employee_role: editing.employee_role || "",
        team_section: editing.team_section || "",
        khojbyte_email: editing.khojbyte_email || "",
        is_intern: secondary.includes("intern"),
        secondary_roles: secondary,
        specialization_taxonomy_id: editing.specialization_taxonomy_id || null,
        supervisor_user_id: editing.supervisor_user_id || null,
        unit_id: editing.unit_id || null,
      };
      const r = await api.post(`/admin/users/${editing.user_id}/employee-profile`, payload);
      toast.success("Employee profile saved");
      setEditing(null);
      // Reflect in local list without a full reload
      setUsers((arr) => arr.map((u) => u.user_id === r.data ? u : (
        u.user_id === editing.user_id ? { ...u, role: r.data.role || u.role, employee_role: r.data.employee_role, team_section: r.data.team_section, khojbyte_email: r.data.khojbyte_email, secondary_roles: r.data.secondary_roles ?? u.secondary_roles } : u
      )));
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">USERS & ROLES</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Manage <em className="italic text-kb-primary">platform users</em></h1>

      <div className="mt-8 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
          <input className="kb-input pl-9" placeholder="Search by name or email…" value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" />
        </div>
        <select className="kb-input lg:w-56" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role-filter">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="mt-6 border border-slate-200 rounded-sm bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 bg-slate-50 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          <div className="col-span-4">Name / Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-3">Affiliation</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
          users.length === 0 ? <div className="p-10 text-center text-sm text-kb-ink-muted">No users found.</div> :
          pager.pageItems.map((u) => (
            <div key={u.user_id} className="grid grid-cols-12 px-4 lg:px-6 py-4 border-b border-slate-200 last:border-0 items-center" data-testid={`admin-user-${u.user_id}`}>
              <div className="col-span-12 lg:col-span-4">
                <UserProfileLink userId={u.user_id} name={u.name} className="font-medium" />
                <div className="kb-mono text-[10px] text-kb-ink-light">{u.email}</div>
              </div>
              <div className="col-span-6 lg:col-span-2 mt-2 lg:mt-0">
                <select className="kb-input text-sm py-1.5 px-2" value={u.role}
                  onChange={(e) => changeRole(u.user_id, e.target.value)}
                  data-testid={`admin-user-${u.user_id}-role`}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="col-span-6 lg:col-span-3 mt-2 lg:mt-0 text-sm text-kb-ink-muted truncate">{u.affiliation || "—"}</div>
              <div className="col-span-6 lg:col-span-2 mt-2 lg:mt-0 kb-mono text-xs text-kb-ink-muted">{new Date(u.created_at).toLocaleDateString()}</div>
              <div className="col-span-6 lg:col-span-1 mt-2 lg:mt-0 flex justify-end items-center gap-2">
                {u.email_verified ? (
                  <span className="kb-mono text-[10px] text-emerald-600" title="Email verified">✓</span>
                ) : (
                  <button
                    onClick={() => manualVerifyEmail(u)}
                    className="p-1 text-amber-600 hover:bg-amber-50 rounded-sm"
                    title="Manually mark email as verified"
                    data-testid={`admin-user-${u.user_id}-verify-email`}
                  >
                    <MailCheck className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => setEditing({
                    user_id: u.user_id,
                    name: u.name,
                    employee_role: u.employee_role || "",
                    team_section: u.team_section || "",
                    khojbyte_email: u.khojbyte_email || "",
                    is_intern: !!u.is_intern,
                    secondary_roles: (() => {
                      // Backfill: if legacy `is_intern=true` was set but the
                      // multi-role picker doesn't yet list "intern", add it so
                      // the checkbox visibly reflects reality on first open.
                      const base = Array.isArray(u.secondary_roles) ? [...u.secondary_roles] : [];
                      if (u.is_intern && !base.includes("intern")) base.push("intern");
                      return base;
                    })(),
                    specialization_taxonomy_id: u.specialization_taxonomy_id || null,
                    specialization_path: u.specialization_path || [],
                    supervisor_user_id: u.supervisor_user_id || null,
                    unit_id: u.unit_id || null,
                  })}
                  className="p-1.5 text-kb-primary hover:bg-kb-subtle rounded-sm"
                  title="Assign employee role / team / @khojbyte.com email"
                  data-testid={`admin-user-${u.user_id}-employee-btn`}
                >
                  <Briefcase className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => deleteUser(u)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-sm" title="Delete user" data-testid={`admin-user-${u.user_id}-delete`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
      </div>
      <Pager {...pager} label="users" testId="admin-users-pager" />

      {/* ----- Employee profile modal ----- */}
      {editing && (
        <div className="fixed inset-0 z-[150] bg-black/40 flex items-center justify-center p-4" data-testid="employee-profile-modal">
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-serif text-lg text-kb-ink">Employee profile · {editing.name}</h3>
              <button onClick={() => setEditing(null)} className="p-1 text-kb-ink-muted hover:text-kb-ink">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Employee role (predefined or custom)</label>
                <input
                  list="emp-role-options"
                  className="kb-input mt-1"
                  placeholder="e.g. CEO, Director, Marketing Head, or custom"
                  value={editing.employee_role}
                  onChange={(e) => setEditing({ ...editing, employee_role: e.target.value })}
                  data-testid="employee-profile-role"
                />
                <datalist id="emp-role-options">
                  {PREDEFINED_EMP_ROLES.map((r) => <option key={r} value={r} />)}
                </datalist>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Organisational team</label>
                <input
                  list="emp-team-options"
                  className="kb-input mt-1"
                  placeholder="e.g. Board of Directors, Executive Team, Marketing"
                  value={editing.team_section}
                  onChange={(e) => setEditing({ ...editing, team_section: e.target.value })}
                  data-testid="employee-profile-team"
                />
                <datalist id="emp-team-options">
                  {PREDEFINED_TEAMS.map((t) => <option key={t} value={t} />)}
                </datalist>
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">
                  Setting a team here <strong>auto-links the user to a matching Org Unit</strong> (created if it doesn&apos;t exist) so they appear at <a href="/admin/org" className="text-kb-primary underline">Org Units</a> and on the public <a href="/about" className="text-kb-primary underline">/about</a> page. For a curated public profile card, also add them under <a href="/admin/team-roles" className="text-kb-primary underline">Team roles</a>.
                </p>
              </div>
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">@khojbyte.com email account</label>
                <input
                  className="kb-input mt-1"
                  placeholder="firstname.lastname (auto-suffixes @khojbyte.com)"
                  value={editing.khojbyte_email}
                  onChange={(e) => setEditing({ ...editing, khojbyte_email: e.target.value })}
                  data-testid="employee-profile-email"
                />
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">Leave blank to auto-generate from the employee&apos;s name.</p>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Additional roles (multi-role access)</label>
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">
                  Grant extra capabilities on top of the primary role — e.g. Employee + Mentor lets the user access both the Task Board and the Mentor console.
                </p>
                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  {SECONDARY_ROLE_OPTIONS.map((r) => (
                    <label key={r} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`employee-profile-secondary-${r}`}>
                      <input
                        type="checkbox"
                        checked={(editing.secondary_roles || []).includes(r)}
                        onChange={(e) => {
                          const cur = new Set(editing.secondary_roles || []);
                          e.target.checked ? cur.add(r) : cur.delete(r);
                          setEditing({ ...editing, secondary_roles: Array.from(cur) });
                        }}
                        className="w-4 h-4 accent-kb-primary"
                      />
                      <span className="capitalize">{r.replace("_", " ")}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Research specialization (hierarchical taxonomy)</label>
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">
                  Pick the deepest node that applies (e.g. Computer Science › AI/ML › LLM). Task assignment by domain will automatically include this user for the picked node and every ancestor.
                </p>
                <div className="mt-2">
                  <TaxonomyPicker
                    domain="both"
                    value={editing.specialization_taxonomy_id}
                    onChange={(id) => setEditing({ ...editing, specialization_taxonomy_id: id })}
                  />
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Reporting line</label>
                <p className="kb-mono text-[10px] text-kb-ink-muted mt-1">
                  Supervisor is the direct manager (sees this user&apos;s tasks &amp; attendance). Unit anchors this user in the org tree.
                </p>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Supervisor</label>
                    <select className="kb-input mt-1" value={editing.supervisor_user_id || ""} onChange={(e) => setEditing({ ...editing, supervisor_user_id: e.target.value || null })} data-testid="employee-profile-supervisor">
                      <option value="">— none —</option>
                      {users.filter((u) => u.user_id !== editing.user_id && ["employee", "admin", "super_admin", "mentor"].includes(u.role)).map((u) => (
                        <option key={u.user_id} value={u.user_id}>{u.name || u.email}{u.employee_role ? ` · ${u.employee_role}` : ""}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Org unit</label>
                    <select className="kb-input mt-1" value={editing.unit_id || ""} onChange={(e) => setEditing({ ...editing, unit_id: e.target.value || null })} data-testid="employee-profile-unit">
                      <option value="">— unassigned —</option>
                      {(unitOptions || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="kb-btn-outline text-xs">Cancel</button>
              <button onClick={saveEmployeeProfile} className="kb-btn-primary text-xs" data-testid="employee-profile-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}